import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { NotFoundPage } from '../../../../app/_components/pages/NotFound.client'
import { TrackPageView } from '../../../../app/_providers/TrackingProvider.client'
import { IterableContainer } from '../../../../containers/iterable'
import { usePagesDirectoryStableQueryParams } from '../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { singleQueryParamValue } from '../../../../lib/utils'

import { usePdpDeprecatedServerSideProps } from './PdpDeprecatedServerSidePropsProvider'
import { usePdpServerSideProps } from './PdpServerSidePropsProvider'
import * as ProductSkeleton from './ProductPageSkeleton/ProductPageSkeleton'
import { ProductPageTemplate } from './ProductPageTemplate'

export const ProductPage = () => {
  const { status: pdpServerSidePropsStatus, data: pdpServerSideProps } = usePdpServerSideProps()
  const { status: pdpDeprecatedServerSidePropsStatus, data: pdpDeprecatedServerSideProps } =
    usePdpDeprecatedServerSideProps()
  const { trackPdpView, isIterableReady } = IterableContainer.useContainer()
  const { asPath } = useRouter()
  const {
    query: { size: queryParamSizeUnsanitized },
  } = usePagesDirectoryStableQueryParams()
  const queryParamSize = singleQueryParamValue(queryParamSizeUnsanitized, undefined)

  useEffect(() => {
    const isReady = isIterableReady && pdpServerSidePropsStatus === 'resolved'

    // only fire page view when new product data is loaded
    if (isReady) {
      const product = pdpServerSideProps.product
      const variantGroup = pdpServerSideProps.product.variantGroup
      const defaultVariant = pdpServerSideProps.product.variantGroup.defaultVariant
      const variant = variantGroup.variant
      const selectedColor = pdpServerSideProps.product.variantGroup.color?.id ?? ''
      const selectedFit = variant?.fit?.id ?? ''
      const selectedSize = queryParamSize ?? ''
      const path = asPath.split('?')[0] ?? asPath
      trackPdpView({
        handle: product.handle,
        defaultImage: variantGroup.images?.[0]?.url ?? '',
        path,
        productUrl: variant?.urlPath ?? variantGroup.urlPath,
        productName: variantGroup.title,
        selectedColor,
        selectedFit,
        selectedSize,
        availability: (variant?.availability ?? variantGroup.availability) === 'AVAILABLE',
        category: variantGroup.primaryCategory.analyticsId,
        productId: variant?.shopifyProductId ?? defaultVariant.shopifyProductId,
        variantId: variant?.shopifyId ?? '',
        productGender: variantGroup.gender,
      })
    }
  }, [
    asPath,
    isIterableReady,
    pdpServerSideProps?.product,
    pdpServerSidePropsStatus,
    queryParamSize,
    trackPdpView,
  ])

  if (!pdpDeprecatedServerSideProps?.productGroup?.featuredProduct || !pdpServerSideProps) {
    if (
      pdpDeprecatedServerSidePropsStatus === 'pending' ||
      pdpDeprecatedServerSidePropsStatus === 'idle' ||
      pdpServerSidePropsStatus === 'pending' ||
      pdpServerSidePropsStatus === 'idle'
    ) {
      return (
        <ProductSkeleton.Wrapper>
          <ProductSkeleton.PrimaryContent>
            <ProductSkeleton.Gallery withFitModelCaption />
            <ProductSkeleton.Details />
          </ProductSkeleton.PrimaryContent>
          <ProductSkeleton.WhyWeLove />
          <ProductSkeleton.FigsScrubsAre />
        </ProductSkeleton.Wrapper>
      )
    } else {
      return <NotFoundPage />
    }
  }

  return (
    <>
      <TrackPageView
        pageViewHandle={pdpServerSideProps.product.handle}
        pageViewCategory='product'
        productShopifyId={
          pdpServerSideProps.product.variantGroup.variant?.shopifyProductId ??
          pdpServerSideProps.product.variantGroup.defaultVariant.shopifyProductId
        }
      />
      <ProductPageTemplate />
    </>
  )
}
